import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ModalHelper } from '@helpers/modal-helper';
import { ApiClientService } from '@services/api-client/api-client.service';
import { VerifyFirmwareUpgradeResponse } from '@services/api-client/responses/verify-firmware-upgrade-response.type';
import { DeviceCalibrationCompletion } from '@services/api-client/requests/verify-firmware-upgrade-request.type';
import { SessionStorageService } from '@services/session-storage/session-storage.service';
import { ConfirmModal } from '@shared/components/confirm-modal/confirm-modal.model';
import { ToastService } from '@services/toast/toast.service';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { DeviceCalibrationStatus } from 'app/enums/device-calibration-status.enum';
import { DateTime } from 'luxon';
import { BehaviorSubject, firstValueFrom, Subject, Subscription, takeUntil, timeout } from 'rxjs';
import { DeviceInfoService } from './device-info.service';
import type { DeviceInfo } from './device-verification.service';
import { SerialService } from './serial.service';

const CLOCK_SYNC_CONFIRMATION_SECONDS = 3600;

export type DeviceClockSyncOutcome =
    | 'skipped'
    | 'declined'
    | 'synchronized'
    | 'failed'
    | 'baseline-reset-failed'
    | 'calibrated';

export type DeviceClockSyncOptions = {
    showCalibrationPrompts?: boolean;
};

type PendingCalibrationOperation = {
    adjustment: DeviceCalibrationCompletion;
    deviceDateTime: string | null;
};

@Injectable({ providedIn: 'root' })
export class DeviceClockSyncService {
    private readonly synchronizing = new BehaviorSubject(false);
    public readonly isSynchronizing$ = this.synchronizing.asObservable();

    public constructor(
        private readonly deviceInfoService: DeviceInfoService,
        private readonly apiClient: ApiClientService,
        private readonly modalService: NgbModal,
        private readonly translateService: TranslateService,
        private readonly toastService: ToastService,
        private readonly router: Router,
        private readonly storage: SessionStorageService
    ) {}

    public get isSynchronizing(): boolean {
        return this.synchronizing.value;
    }

    public getPendingAdjustment(serialNumber: string): DeviceCalibrationCompletion | null {
        const pending = this.storage.getObject<PendingCalibrationOperation>(this.pendingKey(serialNumber));
        return pending?.adjustment ?? null;
    }

    public async synchronize(
        serialService: SerialService,
        deviceInfo: DeviceInfo,
        verification: VerifyFirmwareUpgradeResponse,
        options: DeviceClockSyncOptions = {}
    ): Promise<DeviceClockSyncOutcome> {
        if (this.isSynchronizing || !serialService.isConnected()) {
            return 'skipped';
        }

        if (verification.calibrationCompleted) {
            this.storage.setObject(this.pendingKey(deviceInfo.serialNumber), null);
            this.notify('CALIBRATION_SUCCESS', 'CALIBRATION_TITLE');
            return 'calibrated';
        }
        const driftTooHigh = verification.deviceCalibrationStatusId === DeviceCalibrationStatus.DriftTooHigh;
        const needsCalibration =
            verification.deviceCalibrationStatusId === DeviceCalibrationStatus.EligibleForCalibration ||
            !!verification.pendingCalibration ||
            !!this.getPendingAdjustment(deviceInfo.serialNumber);
        if (
            !driftTooHigh &&
            !needsCalibration &&
            verification.deviceCalibrationStatusId !== DeviceCalibrationStatus.BaselineRecorded
        )
            return 'skipped';
        // Calibration prompts are shown by the calibration section on the upgrade page, which calls calibrate on request
        if (options.showCalibrationPrompts === false && (driftTooHigh || needsCalibration)) return 'skipped';
        const difference = driftTooHigh ? 0 : this.deviceInfoService.getDeviceClockDifference(deviceInfo);
        if (!driftTooHigh && !needsCalibration && (difference === null || difference === 0)) return 'skipped';

        this.synchronizing.next(true);
        const connectionSession = serialService.connectionSessionId;
        const subscriptions = new Subscription();
        const cancelled$ = new Subject<void>();
        let cancelled = false;
        let modal: NgbModalRef | undefined;
        let clockConfirmed = false;
        const cancel = () => {
            cancelled = true;
            cancelled$.next();
            modal?.dismiss('Clock synchronization cancelled');
        };

        subscriptions.add(
            serialService.connectionStatus$.subscribe((connected) => {
                if (!connected) cancel();
            })
        );
        subscriptions.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationStart) cancel();
            })
        );

        try {
            if (cancelled) return 'skipped';
            const canContinue = () =>
                !cancelled && serialService.isConnected() && serialService.connectionSessionId === connectionSession;
            if (driftTooHigh) {
                modal = this.modalService.open(ConfirmModalComponent);
                const helper = ModalHelper.getConfirmModalInstanceHelper(modal);
                helper.componentInstance.model = {
                    titleLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.CALIBRATION_TITLE',
                    bodyLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.DRIFT_TOO_HIGH',
                    bodyParams: { serialNumber: deviceInfo.serialNumber },
                    confirmLanguageKey: 'BUTTONS.OK'
                };
                await firstValueFrom(helper.confirmation$.pipe(takeUntil(cancelled$)));
                return 'skipped';
            }
            if (needsCalibration) {
                return await this.calibrate(serialService, deviceInfo, verification, canContinue, async (model) => {
                    modal = this.modalService.open(ConfirmModalComponent);
                    const helper = ModalHelper.getConfirmModalInstanceHelper(modal);
                    helper.componentInstance.model = model;
                    return await firstValueFrom(helper.confirmation$.pipe(takeUntil(cancelled$)));
                });
            }
            if (Math.abs(difference) >= CLOCK_SYNC_CONFIRMATION_SECONDS) {
                modal = this.modalService.open(ConfirmModalComponent);
                const helper = ModalHelper.getConfirmModalInstanceHelper(modal);
                helper.componentInstance.model = {
                    titleLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.TITLE',
                    bodyLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.CONFIRM_MESSAGE',
                    bodyParams: {
                        serialNumber: deviceInfo.serialNumber,
                        deviceTime: DateTime.fromISO(deviceInfo.deviceDateTime, { zone: 'utc' }).toFormat(
                            'yyyy-MM-dd HH:mm:ss'
                        ),
                        localTime: DateTime.local().toFormat('yyyy-MM-dd HH:mm:ss'),
                        timezone: DateTime.local().zoneName,
                        difference: Math.round(Math.abs(difference) / 60)
                    },
                    cancelLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.KEEP_TIME',
                    confirmLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.SYNC_CLOCK'
                };
                const confirmed = await firstValueFrom(helper.confirmation$.pipe(takeUntil(cancelled$)));
                if (!confirmed) return 'declined';
            }

            if (!canContinue()) return 'skipped';
            const reading = await this.deviceInfoService.setDeviceDateTime(
                serialService,
                deviceInfo.softwareVersion,
                canContinue
            );
            if (!canContinue()) return 'skipped';
            clockConfirmed = true;

            const reset = await firstValueFrom(
                this.apiClient
                    .verifyFirmwareUpgrade$({
                        softwareVersion: deviceInfo.softwareVersion,
                        versionText: deviceInfo.versionText,
                        serialNumber: deviceInfo.serialNumber,
                        deviceDateTime: reading.deviceDateTime,
                        resetCalibrationBaseline: true
                    })
                    .pipe(takeUntil(cancelled$))
            );
            if (
                !reset.calibrationBaselineReset ||
                reset.deviceCalibrationStatusId !== DeviceCalibrationStatus.BaselineRecorded
            ) {
                throw new Error('The server did not confirm the new calibration baseline');
            }

            this.notify('SUCCESS');
            return 'synchronized';
        } catch (error: unknown) {
            if (cancelled) return 'skipped';
            console.warn('[DeviceClockSync] Clock synchronization failed:', error);
            this.notify(clockConfirmed ? 'BASELINE_RESET_FAILED' : 'FAILED');
            return clockConfirmed ? 'baseline-reset-failed' : 'failed';
        } finally {
            subscriptions.unsubscribe();
            cancelled$.complete();
            this.synchronizing.next(false);
        }
    }

    private async calibrate(
        serialService: SerialService,
        deviceInfo: DeviceInfo,
        verification: VerifyFirmwareUpgradeResponse,
        canContinue: () => boolean,
        confirm: (model: ConfirmModal) => Promise<boolean>
    ): Promise<DeviceClockSyncOutcome> {
        let pending = this.storage.getObject<PendingCalibrationOperation>(this.pendingKey(deviceInfo.serialNumber));
        if (!pending && verification.pendingCalibration) {
            pending = { adjustment: { ...verification.pendingCalibration }, deviceDateTime: null };
        }

        try {
            if (!pending && (verification.driftPPM == null || !Number.isFinite(verification.driftPPM))) {
                throw new Error('A measured calibration record and signed PPM are required');
            }
            const confirmed = await confirm({
                titleLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.CALIBRATION_TITLE',
                bodyLanguageKey: pending
                    ? 'FEATURES.DEVICE.CLOCK_SYNC.CALIBRATION_RESUME'
                    : 'FEATURES.DEVICE.CLOCK_SYNC.CALIBRATION_CONFIRM',
                bodyParams: { serialNumber: deviceInfo.serialNumber, ppm: verification.driftPPM?.toFixed(2) ?? '-' },
                cancelLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.NOT_NOW',
                confirmLanguageKey: 'FEATURES.DEVICE.CLOCK_SYNC.CALIBRATE'
            });
            if (!confirmed) return 'declined';
            if (!canContinue()) return 'skipped';
            await this.deviceInfoService.prepareDeviceMaintenance(serialService, canContinue);
            const hardwareCorrection = await this.deviceInfoService.getDeviceCalibration(
                serialService,
                deviceInfo.softwareVersion,
                canContinue
            );

            if (!pending) {
                const adjustment: DeviceCalibrationCompletion = {
                    previousHardwareCorrectionPPM: hardwareCorrection,
                    targetOffsetPPM: this.deviceInfoService.calculateCalibrationTarget(
                        hardwareCorrection,
                        verification.driftPPM
                    ),
                    hardwareCorrectionPPM: null,
                    clockSynchronized: false
                };
                const correction = await this.deviceInfoService.setDeviceCalibration(
                    serialService,
                    deviceInfo.softwareVersion,
                    adjustment.targetOffsetPPM,
                    canContinue,
                    () => {
                        const operation: PendingCalibrationOperation = { adjustment, deviceDateTime: null };
                        this.storage.setObject(this.pendingKey(deviceInfo.serialNumber), operation);
                        pending = operation;
                    }
                );
                pending.adjustment.hardwareCorrectionPPM = correction;
            } else {
                if (
                    this.deviceInfoService.isDeviceCalibrationMatch(
                        hardwareCorrection,
                        pending.adjustment.targetOffsetPPM
                    )
                ) {
                    pending.adjustment.hardwareCorrectionPPM = hardwareCorrection;
                } else if (
                    this.deviceInfoService.isCalibrationCorrectionMatch(
                        hardwareCorrection,
                        pending.adjustment.previousHardwareCorrectionPPM
                    )
                ) {
                    console.log(
                        '[DeviceClockSync] Hardware still has the previous correction; retrying the saved absolute target.'
                    );
                    pending.adjustment.hardwareCorrectionPPM = await this.deviceInfoService.setDeviceCalibration(
                        serialService,
                        deviceInfo.softwareVersion,
                        pending.adjustment.targetOffsetPPM,
                        canContinue,
                        () => undefined
                    );
                } else {
                    throw new Error(
                        'The hardware correction is neither the saved previous value nor the saved target; refusing to apply another correction'
                    );
                }
            }

            this.storage.setObject(this.pendingKey(deviceInfo.serialNumber), pending);
            const reading = await this.deviceInfoService.setDeviceDateTime(
                serialService,
                deviceInfo.softwareVersion,
                canContinue
            );
            if (!canContinue()) throw new Error('Device calibration was interrupted');
            pending.adjustment.clockSynchronized = true;
            pending.deviceDateTime = reading.deviceDateTime;
            this.storage.setObject(this.pendingKey(deviceInfo.serialNumber), pending);
            const completion = await this.notifyCalibrationServer(deviceInfo, pending);
            if (!completion.calibrationCompleted)
                throw new Error('The server did not acknowledge calibration completion');
            this.storage.setObject(this.pendingKey(deviceInfo.serialNumber), null);
            this.notify('CALIBRATION_SUCCESS', 'CALIBRATION_TITLE');
            return 'calibrated';
        } catch (error: unknown) {
            console.warn('[DeviceClockSync] Device calibration did not complete:', error);
            if (pending && !pending.adjustment.clockSynchronized) {
                try {
                    await this.notifyCalibrationServer(deviceInfo, pending);
                } catch (notificationError: unknown) {
                    console.warn('[DeviceClockSync] Could not record pending calibration:', notificationError);
                }
            }
            if (pending) this.notify('CALIBRATION_PENDING', 'CALIBRATION_TITLE');
            else if (canContinue()) this.notify('CALIBRATION_FAILED', 'CALIBRATION_TITLE');
            return 'failed';
        }
    }

    private async notifyCalibrationServer(
        deviceInfo: DeviceInfo,
        pending: PendingCalibrationOperation
    ): Promise<VerifyFirmwareUpgradeResponse> {
        return await firstValueFrom(
            this.apiClient
                .verifyFirmwareUpgrade$({
                    softwareVersion: deviceInfo.softwareVersion,
                    versionText: deviceInfo.versionText,
                    serialNumber: deviceInfo.serialNumber,
                    deviceDateTime: pending.deviceDateTime,
                    calibration: pending.adjustment
                })
                .pipe(timeout(10000))
        );
    }

    private pendingKey(serialNumber: string): string {
        return `device-calibration-pending:${serialNumber}`;
    }

    private notify(messageKey: string, titleKey = 'TITLE'): void {
        this.toastService.open(
            this.translateService.instant(`FEATURES.DEVICE.CLOCK_SYNC.${titleKey}`) as string,
            this.translateService.instant(`FEATURES.DEVICE.CLOCK_SYNC.${messageKey}`) as string
        );
    }
}
